/**
 * JSON:API `included[]` → job-queue reconcile adapter (WEB-L1).
 *
 * Service-layer fetch paths that load concepts/angles with their renders
 * pipe their response's `included` array through this helper. It filters
 * to `type: 'render'` entries and calls
 * `useJobQueueStore.getState().reconcileFromApi(...)` once per response.
 *
 * Decoupled from a specific service so concepts / angles / future endpoints
 * all share the same plumbing. See docs/REALTIME_RECONCILE_PLAN.md → WEB-L1.
 */

import type { Render } from '@/types/render';
import { useJobQueueStore } from './job-queue.store';

interface JsonApiIncludedEntry {
  type?: string;
  id?: string;
  attributes?: { status?: unknown };
}

function isRenderEntry(entry: JsonApiIncludedEntry): entry is Render {
  return (
    entry.type === 'render' &&
    typeof entry.id === 'string' &&
    !!entry.attributes &&
    typeof entry.attributes.status === 'string'
  );
}

/**
 * Extract render entries from a JSON:API response's `included[]` array and
 * reconcile them into the job-queue store. Heals both lost-completed and
 * lost-started transitions (`reconcileFromApi` contract).
 *
 * Safe to call with `undefined` / empty / non-render-bearing responses —
 * each guard fast-returns. The store action itself is idempotent and
 * never seeds new jobs (heal-only, per WEB-L1 contract).
 */
export function reconcileRendersFromJsonApi(
  included: ReadonlyArray<unknown> | undefined
): void {
  if (!included || included.length === 0) return;

  const renders: Render[] = [];
  for (const entry of included) {
    if (typeof entry !== 'object' || entry === null) continue;
    const typed = entry as JsonApiIncludedEntry;
    if (isRenderEntry(typed)) {
      renders.push(typed);
    }
  }

  if (renders.length === 0) return;

  useJobQueueStore.getState().reconcileFromApi(renders);
}
